import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Star,
  Trophy,
  Clock,
  Brain,
  Lightbulb,
  Target,
  Coins,
} from "lucide-react";
import { getCurrentUser } from "../utils/auth";
import Layout from "../components/Layout";
import { QuizQuestion } from "../types";

const QuizPage: React.FC = () => {
  useParams(); // Para manter a funcionalidade de roteamento
  const [user] = useState(getCurrentUser());
  const navigate = useNavigate();

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);

  // Dados do quiz (simulados)
  const [questions] = useState<QuizQuestion[]>([
    {
      id: "1",
      question: "What is a personal budget?",
      options: [
        "A document that lists only your expenses",
        "A financial plan that controls income and expenses",
        "A special bank account",
        "A type of investment",
      ],
      correctAnswer: 1,
      explanation:
        "A personal budget is a financial plan that helps you control your income and expenses, allowing better management of your money.",
      difficulty: "easy",
    },
    {
      id: "2",
      question: "What is the 50-30-20 rule?",
      options: [
        "50% for needs, 30% for wants, 20% for savings",
        "50% for expenses, 30% for investments, 20% for emergency",
        "50% for house, 30% for food, 20% for transportation",
        "50% for salary, 30% for bonus, 20% for commissions",
      ],
      correctAnswer: 0,
      explanation:
        "The 50-30-20 rule suggests allocating 50% of income for needs, 30% for wants and 20% for savings and investments.",
      difficulty: "medium",
    },
    {
      id: "3",
      question: "What are compound interest?",
      options: [
        "Interest calculated only on the principal",
        "Interest calculated on the principal plus accumulated interest",
        "Interest with fixed rate",
        "Interest paid monthly",
      ],
      correctAnswer: 1,
      explanation:
        "Compound interest is calculated on the principal plus accumulated interest, creating an exponential growth effect.",
      difficulty: "medium",
    },
    {
      id: "4",
      question: "What is the importance of having an emergency reserve?",
      options: [
        "To make high-risk investments",
        "To cover unexpected expenses without compromising the budget",
        "To pay only basic bills",
        "To buy luxury items",
      ],
      correctAnswer: 1,
      explanation:
        "An emergency reserve serves to cover unexpected expenses, such as unemployment or health problems, without compromising your financial planning.",
      difficulty: "easy",
    },
    {
      id: "5",
      question: "What is inflation?",
      options: [
        "Increase in the value of money",
        "General increase in the prices of goods and services",
        "Decrease in salaries",
        "Increase in bank interest rates",
      ],
      correctAnswer: 1,
      explanation:
        "Inflation is the general and continuous increase in the prices of goods and services, reducing the purchasing power of money.",
      difficulty: "hard",
    },
  ]);

  const currentQuestion = questions[currentQuestionIndex];

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    // Timer para cada pergunta
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [user, navigate]);

  const handleAnswerSelect = (answerIndex: number) => {
    if (showResult) return;

    setSelectedAnswer(answerIndex);
    setShowResult(true);

    if (answerIndex === currentQuestion.correctAnswer) {
      setScore(score + 1);
      setStreak(streak + 1);
      setMaxStreak(Math.max(maxStreak, streak + 1));
    } else {
      setStreak(0);
    }
  };

  const handleTimeUp = () => {
    if (showResult) return;

    setSelectedAnswer(-1); // -1 indica tempo esgotado
    setShowResult(true);
    setStreak(0);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setTimeLeft(30);
    } else {
      setQuizCompleted(true);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "easy":
        return "text-green-400";
      case "medium":
        return "text-yellow-400";
      case "hard":
        return "text-red-400";
      default:
        return "text-gray-400";
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case "easy":
        return "🟢";
      case "medium":
        return "🟡";
      case "hard":
        return "🔴";
      default:
        return "⚪";
    }
  };

  const getScoreMessage = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage >= 80)
      return { message: "Excellent!", color: "text-green-400" };
    if (percentage >= 60)
      return { message: "Very good!", color: "text-blue-400" };
    if (percentage >= 40)
      return {
        message: "Good work!",
        color: "text-yellow-400",
      };
    return {
      message: "Keep studying!",
      color: "text-orange-400",
    };
  };

  const calculateStars = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage >= 80) return 3;
    if (percentage >= 60) return 2;
    if (percentage >= 40) return 1;
    return 0;
  };

  if (!user) {
    return null;
  }

  if (quizCompleted) {
    const scoreData = getScoreMessage();
    const starsEarned = calculateStars();

    return (
      <Layout
        title="Quiz Completed!"
        subtitle="Congratulations on completing the quiz"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto"
        >
          {/* Resultado principal */}
          <div className="card text-center mb-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-6xl mb-4"
            >
              <Trophy className="w-16 h-16 text-yellow-400 mx-auto" />
            </motion.div>

            <h2 className={`text-2xl font-bold mb-2 ${scoreData.color}`}>
              {scoreData.message}
            </h2>

            <p className="text-gray-400 mb-6">
              Você acertou {score} de {questions.length} perguntas
            </p>

            {/* Estrelas conquistadas */}
            <div className="flex justify-center space-x-1 sm:space-x-2 mb-4 sm:mb-6">
              {[1, 2, 3].map((star) => (
                <motion.div
                  key={star}
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{
                    scale: star <= starsEarned ? 1 : 0.5,
                    rotate: 0,
                  }}
                  transition={{
                    duration: 0.3,
                    delay: 0.5 + star * 0.1,
                  }}
                  className={`text-3xl ${
                    star <= starsEarned ? "text-yellow-400" : "text-gray-600"
                  }`}
                >
                  <Star className="w-8 h-8 text-yellow-400" />
                </motion.div>
              ))}
            </div>

            {/* Estatísticas */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-dark-700 rounded-lg p-2 sm:p-4">
                <div className="text-lg sm:text-2xl font-bold text-white">
                  {score}
                </div>
                <div className="text-xs sm:text-sm text-gray-400">
                  Correct Answers
                </div>
              </div>
              <div className="bg-dark-700 rounded-lg p-2 sm:p-4">
                <div className="text-lg sm:text-2xl font-bold text-white">
                  {maxStreak}
                </div>
                <div className="text-xs sm:text-sm text-gray-400">Streak</div>
              </div>
              <div className="bg-dark-700 rounded-lg p-2 sm:p-4">
                <div className="text-lg sm:text-2xl font-bold text-white">
                  {Math.round((score / questions.length) * 100)}%
                </div>
                <div className="text-xs sm:text-sm text-gray-400">Accuracy</div>
              </div>
            </div>

            {/* Recompensas */}
            <div className="bg-gradient-to-r from-primary-600/20 to-blue-600/20 border border-primary-500/30 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-white mb-3">
                Rewards Conquered
              </h3>
              <div className="flex justify-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <span className="text-white font-semibold">
                    +{starsEarned * 10}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Coins className="w-6 h-6 text-green-400" />
                  <span className="text-white font-semibold">+{score * 5}</span>
                </div>
                {maxStreak >= 3 && (
                  <div className="flex items-center space-x-2">
                    <Trophy className="w-5 h-5 text-yellow-400" />
                    <span className="text-white font-semibold">Streak!</span>
                  </div>
                )}
              </div>
            </div>

            {/* Botões de ação */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => navigate("/finance-school")}
                className="flex-1 py-3 bg-dark-700 hover:bg-dark-600 text-white font-medium rounded-lg transition-colors"
              >
                Back to Finance School
              </button>
              <button
                onClick={() => {
                  setCurrentQuestionIndex(0);
                  setSelectedAnswer(null);
                  setShowResult(false);
                  setScore(0);
                  setStreak(0);
                  setMaxStreak(0);
                  setQuizCompleted(false);
                  setTimeLeft(30);
                }}
                className="flex-1 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
              >
                New Quiz
              </button>
            </div>
          </div>
        </motion.div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Financial Quiz"
      subtitle={`Question ${currentQuestionIndex + 1} of ${questions.length}`}
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl mx-auto"
      >
        {/* Header do quiz */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/finance-school")}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="text-white font-semibold">{score}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-2xl">🔥</span>
              <span className="text-white font-semibold">{streak}</span>
            </div>
          </div>
        </div>

        {/* Barra de progresso */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Progresso</span>
            <span>
              {currentQuestionIndex + 1}/{questions.length}
            </span>
          </div>
          <div className="w-full bg-dark-700 rounded-full h-2">
            <motion.div
              initial={{ width: 0 }}
              animate={{
                width: `${
                  ((currentQuestionIndex + 1) / questions.length) * 100
                }%`,
              }}
              transition={{ duration: 0.3 }}
              className="h-2 bg-gradient-to-r from-primary-500 to-blue-500 rounded-full"
            />
          </div>
        </div>

        {/* Timer */}
        <div className="flex justify-center mb-6">
          <div
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              timeLeft <= 10
                ? "bg-red-600/20 text-red-400"
                : "bg-dark-700 text-white"
            }`}
          >
            <Clock className="w-4 h-4" />
            <span className="font-mono text-lg">{timeLeft}s</span>
          </div>
        </div>

        {/* Pergunta */}
        <motion.div
          key={currentQuestionIndex}
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -50, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="card mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Brain className="w-5 h-5 text-primary-400" />
              <span className="text-sm text-gray-400">Pergunta</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-lg">
                {getDifficultyIcon(currentQuestion.difficulty)}
              </span>
              <span
                className={`text-sm font-medium ${getDifficultyColor(
                  currentQuestion.difficulty
                )}`}
              >
                {currentQuestion.difficulty === "easy" && "Fácil"}
                {currentQuestion.difficulty === "medium" && "Médio"}
                {currentQuestion.difficulty === "hard" && "Difícil"}
              </span>
            </div>
          </div>

          <h2 className="text-xl font-semibold text-white mb-6">
            {currentQuestion.question}
          </h2>

          {/* Opções de resposta */}
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => {
              let buttonClass =
                "w-full p-4 text-left rounded-lg transition-all duration-200 ";

              if (showResult) {
                if (index === currentQuestion.correctAnswer) {
                  buttonClass +=
                    "bg-green-600/20 border-2 border-green-500 text-green-400";
                } else if (
                  index === selectedAnswer &&
                  selectedAnswer !== currentQuestion.correctAnswer
                ) {
                  buttonClass +=
                    "bg-red-600/20 border-2 border-red-500 text-red-400";
                } else {
                  buttonClass += "bg-dark-700 text-gray-400";
                }
              } else {
                buttonClass +=
                  selectedAnswer === index
                    ? "bg-primary-600/20 border-2 border-primary-500 text-primary-400"
                    : "bg-dark-700 hover:bg-dark-600 text-white border-2 border-transparent";
              }

              return (
                <motion.button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={showResult}
                  whileHover={!showResult ? { scale: 1.02 } : {}}
                  whileTap={!showResult ? { scale: 0.98 } : {}}
                  className={buttonClass}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                        showResult && index === currentQuestion.correctAnswer
                          ? "bg-green-500 text-white"
                          : showResult &&
                            index === selectedAnswer &&
                            selectedAnswer !== currentQuestion.correctAnswer
                          ? "bg-red-500 text-white"
                          : "bg-dark-600 text-gray-400"
                      }`}
                    >
                      {String.fromCharCode(65 + index)}
                    </div>
                    <span className="flex-1">{option}</span>
                    {showResult && index === currentQuestion.correctAnswer && (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    )}
                    {showResult &&
                      index === selectedAnswer &&
                      selectedAnswer !== currentQuestion.correctAnswer && (
                        <XCircle className="w-5 h-5 text-red-400" />
                      )}
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Explicação */}
          <AnimatePresence>
            {showResult && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-4 p-4 bg-blue-600/20 border border-blue-500/30 rounded-lg"
              >
                <div className="flex items-start space-x-3">
                  <Lightbulb className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-blue-400 font-semibold mb-2">
                      Explicação:
                    </h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {currentQuestion.explanation}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Botão próxima pergunta */}
        <AnimatePresence>
          {showResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="text-center"
            >
              <button
                onClick={handleNextQuestion}
                className="px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors flex items-center space-x-2 mx-auto"
              >
                <span>
                  {currentQuestionIndex < questions.length - 1
                    ? "Próxima Pergunta"
                    : "Finalizar Quiz"}
                </span>
                <Target className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </Layout>
  );
};

export default QuizPage;
